// Модальный редактор вариантов упражнения text_fill_bank для преподавателей.
// Позволяет разбить текст на пропуски, указать правильные слова и добавить слова-дистракторы для банка слов.

import { useState, useMemo } from 'react';
import {
  Modal, Row, Col, Card, Form, Button, Badge, Table, Alert, Spinner,
} from 'react-bootstrap';

// ── Helpers ────────────────────────────────────────────────────────────────────

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function parseBlanks(text) {
  const regex = /_{5,}/g;
  let match;
  const positions = [];
  let count = 0;
  while ((match = regex.exec(text)) !== null) {
    count += 1;
    positions.push({ position: String(count), index: match.index });
  }
  return { count, positions };
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ManagerTextFillBankVariant({ show, onHide, onSave, editVariant }) {
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState(false);
  const [blankCount, setBlankCount] = useState(0);

  const [correctWords, setCorrectWords] = useState({});
  const [distractors, setDistractors] = useState([]);
  const [distractorInput, setDistractorInput] = useState('');

  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Parse ──────────────────────────────────────────────────────────────────────
  const handleParse = () => {
    const { count } = parseBlanks(rawText);
    setBlankCount(count);
    setCorrectWords((prev) => {
      const next = {};
      for (let i = 1; i <= count; i++) {
        next[String(i)] = prev[String(i)] ?? '';
      }
      return next;
    });
    setParsed(true);
    setSaveError('');
  };

  // ── Preview ────────────────────────────────────────────────────────────────────
  const previewWords = useMemo(() => {
    const correct = Object.values(correctWords).filter((w) => w.trim() !== '');
    return shuffle([...correct, ...distractors]);
  }, [correctWords, distractors]);

  // ── Distractor handlers ────────────────────────────────────────────────────────
  const handleAddDistractor = () => {
    const word = distractorInput.trim();
    if (!word) return;
    if (distractors.includes(word)) { setDistractorInput(''); return; }
    setDistractors((prev) => [...prev, word]);
    setDistractorInput('');
  };

  const handleDistractorKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddDistractor(); }
  };

  const handleRemoveDistractor = (word) => {
    setDistractors((prev) => prev.filter((w) => w !== word));
  };

  // ── Save ────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError('');

    if (!rawText.trim()) { setSaveError('Введите текст упражнения.'); return; }
    if (!parsed || blankCount === 0) {
      setSaveError('Нажмите «Разобрать пропуски» — в тексте не найдено ни одного пропуска.');
      return;
    }
    for (let i = 1; i <= blankCount; i++) {
      if (!correctWords[String(i)]?.trim()) {
        setSaveError(`Заполните правильное слово для пропуска №${i}.`);
        return;
      }
    }

    const answers = {};
    for (let i = 1; i <= blankCount; i++) {
      answers[String(i)] = correctWords[String(i)].trim();
    }
    const allWords = [...Object.values(answers), ...distractors];
    const variants = {};
    allWords.forEach((word, idx) => { variants[String(idx + 1)] = word; });

    setSaving(true);
    try {
      await onSave?.({ question: rawText.trim(), answers, variants });
      onHide();
    } catch (err) {
      setSaveError(err?.response?.data?.detail ?? 'Ошибка при сохранении варианта.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = parsed && blankCount > 0;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {editVariant ? 'Редактировать вариант' : 'Новый вариант — Текст с пропусками'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {saveError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setSaveError('')}
            style={{ borderRadius: '0.75rem', marginBottom: '1.25rem' }}
          >
            {saveError}
          </Alert>
        )}

        <Row className="g-4 align-items-start">

          {/* ── Left: text editor ── */}
          <Col xs={12} md={6}>
            <Card
              className="border-0 h-100"
              style={{ borderRadius: '0.875rem', boxShadow: 'var(--ewl-shadow-sm)', background: '#fff' }}
            >
              <Card.Body className="p-4">
                <div
                  className="fw-semibold mb-3"
                  style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
                >
                  Текст упражнения
                </div>

                <Form.Group className="mb-2">
                  <Form.Control
                    as="textarea"
                    rows={9}
                    value={rawText}
                    onChange={(e) => {
                      setRawText(e.target.value);
                      setParsed(false);
                      setSaveError('');
                    }}
                    placeholder={'Введите текст упражнения.\nОбозначьте пропуски символами _____ (5 подчёркиваний).\n\nНапример:\nThe _____ shines brightly. It was a _____ day outside.'}
                    style={{
                      borderRadius: '0.625rem',
                      fontSize: '0.9375rem',
                      lineHeight: 1.7,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </Form.Group>

                <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                  Обозначайте пропуски символами <code style={{ background: '#f0f2f8', padding: '1px 5px', borderRadius: '0.25rem' }}>_____</code> (не менее 5 подчёркиваний).
                </p>

                <Button
                  onClick={handleParse}
                  disabled={!rawText.trim()}
                  style={{
                    background: rawText.trim()
                      ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)'
                      : undefined,
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    padding: '0.6rem 1.5rem',
                    width: '100%',
                    opacity: rawText.trim() ? 1 : 0.55,
                  }}
                >
                  Разобрать пропуски
                </Button>

                {parsed && (
                  <div
                    className="d-flex align-items-center gap-2 mt-3"
                    style={{
                      background: blankCount > 0 ? 'rgba(79,110,247,0.07)' : '#fff8e7',
                      border: `1px solid ${blankCount > 0 ? 'rgba(79,110,247,0.25)' : '#ffc107'}`,
                      borderRadius: '0.625rem',
                      padding: '0.625rem 1rem',
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: blankCount > 0 ? '#1e2a4a' : '#856404' }}>
                      {blankCount > 0
                        ? `Найдено ${blankCount} пропуск${blankCount === 1 ? '' : blankCount < 5 ? 'а' : 'ов'}`
                        : 'Пропуски не найдены. Добавьте _____ в текст.'}
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* ── Right: word bank ── */}
          <Col xs={12} md={6}>
            {parsed && blankCount > 0 ? (
              <>
                {/* Correct words table */}
                <Card
                  className="border-0 mb-4"
                  style={{ borderRadius: '0.875rem', boxShadow: 'var(--ewl-shadow-sm)', background: '#fff' }}
                >
                  <Card.Body className="p-0">
                    <div className="px-4 pt-4 pb-3">
                      <div
                        className="fw-semibold"
                        style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
                      >
                        Правильные слова
                      </div>
                    </div>

                    <div className="table-wrapper" style={{ borderRadius: '0 0 0.875rem 0.875rem', boxShadow: 'none' }}>
                      <Table className="mb-0" style={{ fontSize: '0.9rem' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '4.5rem', paddingLeft: '1.25rem' }}>Пропуск</th>
                            <th>Правильное слово</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from({ length: blankCount }, (_, i) => {
                            const pos = String(i + 1);
                            const word = correctWords[pos] ?? '';
                            const filled = word.trim() !== '';
                            return (
                              <tr key={pos}>
                                <td style={{ paddingLeft: '1.25rem', verticalAlign: 'middle' }}>
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '1.75rem',
                                      height: '1.75rem',
                                      borderRadius: '50%',
                                      background: filled ? '#4f6ef7' : '#e9ecef',
                                      color: filled ? '#fff' : '#6c757d',
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    {i + 1}
                                  </span>
                                </td>
                                <td style={{ verticalAlign: 'middle', paddingRight: '1.25rem' }}>
                                  <Form.Control
                                    type="text"
                                    value={word}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setCorrectWords((prev) => ({ ...prev, [pos]: val }));
                                      setSaveError('');
                                    }}
                                    placeholder="Введите слово..."
                                    style={{ borderRadius: '0.5rem', fontSize: '0.9rem' }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>

                {/* Distractors */}
                <Card
                  className="border-0 mb-4"
                  style={{ borderRadius: '0.875rem', boxShadow: 'var(--ewl-shadow-sm)', background: '#fff' }}
                >
                  <Card.Body className="p-4">
                    <div
                      className="fw-semibold mb-1"
                      style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
                    >
                      Дополнительные слова (дистракторы)
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '1rem', lineHeight: 1.5 }}>
                      Лишние слова, которые усложнят выбор. Попадут в банк вместе с правильными.
                    </p>

                    {distractors.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {distractors.map((word) => (
                          <span
                            key={word}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              background: '#f0f2f8',
                              border: '1px solid #dee2e6',
                              borderRadius: '2rem',
                              padding: '4px 12px 4px 14px',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: '#1e2a4a',
                            }}
                          >
                            {word}
                            <button
                              type="button"
                              onClick={() => handleRemoveDistractor(word)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: '0 2px',
                                cursor: 'pointer',
                                color: '#adb5bd',
                                lineHeight: 1,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                              title="Удалить"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        value={distractorInput}
                        onChange={(e) => setDistractorInput(e.target.value)}
                        onKeyDown={handleDistractorKeyDown}
                        placeholder="Слово-дистрактор"
                        style={{ borderRadius: '0.5rem', fontSize: '0.9rem' }}
                      />
                      <Button
                        variant="outline-primary"
                        onClick={handleAddDistractor}
                        disabled={!distractorInput.trim()}
                        style={{ borderRadius: '0.5rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        Добавить
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Word bank preview */}
                <Card
                  className="border-0"
                  style={{
                    borderRadius: '0.875rem',
                    boxShadow: 'var(--ewl-shadow-sm)',
                    background: '#fff',
                    border: '1px solid rgba(79,110,247,0.15)',
                  }}
                >
                  <Card.Body className="p-4">
                    <div
                      className="fw-semibold mb-1"
                      style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
                    >
                      Предпросмотр банка слов
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '1rem', lineHeight: 1.5 }}>
                      Так студент увидит слова для вставки (порядок случайный).
                    </p>
                    {previewWords.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {previewWords.map((word, idx) => (
                          <Badge
                            key={idx}
                            bg="primary"
                            style={{
                              background: 'transparent',
                              color: '#4f6ef7',
                              border: '2px solid #4f6ef7',
                              borderRadius: '2rem',
                              padding: '5px 14px',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                            }}
                          >
                            {word}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        Заполните правильные слова выше, чтобы увидеть предпросмотр.
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </>
            ) : (
              /* Placeholder until parsed */
              <Card
                className="border-0 h-100"
                style={{
                  borderRadius: '0.875rem',
                  boxShadow: 'var(--ewl-shadow-sm)',
                  background: '#fff',
                  minHeight: 320,
                }}
              >
                <Card.Body className="p-4 d-flex flex-column align-items-center justify-content-center text-center">
                  <p
                    className="fw-semibold mb-1"
                    style={{ color: '#1e2a4a', fontSize: '0.9375rem' }}
                  >
                    Введите текст и нажмите «Разобрать пропуски»
                  </p>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem', lineHeight: 1.5, maxWidth: 260 }}>
                    После разбора здесь появятся поля для правильных слов и раздел дистракторов.
                  </p>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Отмена</Button>
        <Button
          onClick={handleSave}
          disabled={!canSave || saving}
          style={{
            background: canSave
              ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3e6a 100%)'
              : undefined,
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: 600,
            padding: '0.625rem 1.75rem',
            opacity: canSave ? 1 : 0.55,
          }}
        >
          {saving ? <><Spinner size="sm" animation="border" className="me-2" />Сохраняем...</> : 'Сохранить вариант'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
